import {
    createAsyncThunk,
    createSelector,
    createSlice,
    type PayloadAction,
} from "@reduxjs/toolkit";
import type { UserInfo } from "@foundation/reg-ui-client";

import { fetchNewsletterSubscriptions } from "~api/client/fetchNewsletterSubscriptions";
import type { RootState } from "~redux/store";

export interface UserData extends Partial<UserInfo> {
    /* ===== newsletterData via login.bloomberg.com api ===== */
    isSubscriber?: boolean;
    userNewsletterLists?: string[];
    /* ===== place other user properties here ===== */
    countryCode?: string;
}

type UserCountryData = {
    countryCode?: string;
};

const initialState: UserData = {};

export const updateUserInfo = createAsyncThunk<
    UserData,
    UserInfo | undefined | null
>("user/fetchUserInfo", async (userInfo?: UserInfo | null) => {
    let subscribedNewslettersInfo;
    // eslint-disable-next-line no-console
    console.log("[user-slice][updateUserInfo]: Running Updater");
    const email = userInfo?.user?.email;

    if (email) {
        try {
            subscribedNewslettersInfo = await fetchNewsletterSubscriptions({
                email,
            });
        } catch (e) {
            console.error(`[user-slice][updateUserInfo]: ${e}`);
        }
    }

    return {
        ...userInfo,
        ...(subscribedNewslettersInfo || {}),
    };
});

export const userSelector = (state: RootState) => state.user;

export const selectUserFirstName = createSelector(
    userSelector,
    (user) => user.user?.firstName
);

export const selectUserEmail = createSelector(
    userSelector,
    (user) => user.user?.email
);

export const selectUserSubscribedNewsletters = createSelector(
    userSelector,
    (user) => user.userNewsletterLists
);

export const selectUserCountryCode = createSelector(
    userSelector,
    (user) => user.countryCode
);

const userSlice = createSlice({
    name: "user",
    initialState,
    reducers: {
        userDataFetched(state, action: PayloadAction<UserInfo>) {
            const userInfo = action.payload;
            return {
                ...state,
                userInfo,
            };
        },
        setUserCountry(state, action: PayloadAction<UserCountryData>) {
            const countryCode = action.payload.countryCode;
            return {
                ...state,
                countryCode,
            };
        },
    },
    extraReducers: (builder) => {
        builder.addCase(updateUserInfo.fulfilled, (state, action) => {
            return {
                ...state,
                ...action.payload,
            };
        });
    },
});

export const { userDataFetched, setUserCountry } = userSlice.actions;

export default userSlice.reducer;
